import React from 'react';
import { Button } from '../../../components/ui/Button';
import { Machine } from '../../../types/machines';
import { Badge } from '../../../components/ui/Badge';
import { Video } from '../../../types/videos';

export type DetailViewProps = {
  data: Machine | null;
  videos?: Video[];
  loading?: boolean;
  error?: string | null;
  productCounts?: { active: number; inactive: number };
  onBack: () => void;
  onEdit: (id: string) => void;
  onAssignVideo: () => void;
  onGenerateToken: () => void;
  onGenerateVm: () => void;
  onSyncVideos?: () => void;
  syncingVideos?: boolean;
  generatingToken?: boolean;
  generatingVm?: boolean;
};

export const MachineDetailView: React.FC<DetailViewProps> = ({ data, videos = [], loading, error, productCounts, onBack, onEdit, onAssignVideo, onGenerateToken, onGenerateVm, onSyncVideos, syncingVideos, generatingToken, generatingVm }) => {
  return (
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Machine Details</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onBack}>Back</Button>
          {data && (
            <Button variant="secondary" onClick={onGenerateToken} disabled={!!generatingToken || !!generatingVm || !!loading}>
              {generatingToken ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner /> Generating Token...
                </span>
              ) : (
                'Generate Token'
              )}
            </Button>
          )}
          {data && (
            <Button variant="secondary" onClick={onGenerateVm} disabled={!!generatingVm || !!generatingToken || !!loading}>
              {generatingVm ? (
                <span className="inline-flex items-center gap-2">
                  <Spinner /> Generating VM...
                </span>
              ) : (
                'Generate VM'
              )}
            </Button>
          )}
          {data && <Button onClick={() => onEdit(data.id)} disabled={!!generatingToken || !!generatingVm}>Edit</Button>}
        </div>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div className="text-sm text-destructive">{error}</div>}
      {data && (
        <>
          <div className="rounded border p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="ID" value={String(data.id)} />
              <div>
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="mt-1">
                  <Badge variant={data.status === 'online' ? 'success' : 'destructive'}>{data.status}</Badge>
                </div>
              </div>
              <Field label="Name" value={data.name} />
              <Field label="Location" value={data.location} />
              <Field label="Serial Number" value={data.serialNumber} />
              <Field label="Last Sync" value={data.lastSyncAt ? new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(data.lastSyncAt)) : '-'} />
              {productCounts && (
                <>
                  <Field label="Active Products" value={String(productCounts.active)} />
                  <Field label="Inactive Products" value={String(productCounts.inactive)} />
                </>
              )}
              <div className="md:col-span-2">
                <Field label="Description" value={data.description || '-'} />
              </div>
            </div>
          </div>

          <div className="mt-4 rounded border p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-lg font-semibold">Advertisements</div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onSyncVideos} disabled={!onSyncVideos || !!syncingVideos || !!loading}>
                  {syncingVideos ? (
                    <span className="inline-flex items-center gap-2"><Spinner /> Syncing...</span>
                  ) : (
                    'Sync to Machine'
                  )}
                </Button>
                <Button onClick={onAssignVideo}>Assign Video</Button>
              </div>
            </div>
            <div className="max-h-[50vh] overflow-auto">
              {loading ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
              ) : (videos && videos.length) ? (
                <ul className="divide-y">
                  {videos.map((v, i) => (
                    <li key={v.id} className="flex items-center justify-between gap-2 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{i + 1}. {v.filename}</div>
                        {typeof v.durationSec === 'number' && (
                          <div className="text-xs text-muted-foreground">{v.durationSec}s</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-muted-foreground">No videos assigned.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mt-1 text-sm">{value}</div>
  </div>
);

const Spinner: React.FC = () => (
  <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" role="img">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
  </svg>
);

export default MachineDetailView;
